'use memo'

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

interface Params<T> {
  /** Query param holding the id, e.g. 'team_id' or 'division_id'. */
  param: string
  /**
   * Whether a pre-select may run now: the list this resolves against has
   * loaded, and nothing (an existing choice, a resumed session, another mode)
   * should override the reader.
   */
  enabled: boolean
  /** Turn the param value into something selectable, or null when it isn't. */
  resolve: (value: string) => T | null
  onSelect: (resolved: T) => void
}

/**
 * Apply a registration deep-link param to the wizard, exactly once.
 *
 * The params ride the URL through every step (see `preserveRegistrationParams`)
 * so they are still there when the list they refer to finally loads — which for
 * the team list is two steps after arriving. That persistence is also why this
 * has to latch: the param never leaves the URL (the link stays shareable), so
 * without a latch the effect would re-apply every time the selection went back
 * to empty, overriding a reader who deliberately cleared it.
 */
export const usePreselectFromUrl = <T>({
  param,
  enabled,
  resolve,
  onSelect,
}: Params<T>): void => {
  const { search } = useLocation()
  const hasApplied = useRef(false)
  // Kept current so the effect can call the latest functions without listing
  // them as dependencies. See the note on the dependency array below.
  const resolveRef = useRef(resolve)
  const onSelectRef = useRef(onSelect)
  resolveRef.current = resolve
  onSelectRef.current = onSelect

  useEffect(() => {
    if (hasApplied.current || !enabled) {
      return
    }
    const value = new URLSearchParams(search).get(param)
    if (!value) {
      return
    }
    // The list has loaded and the id is either in it or it isn't. Latch either
    // way: an id missing from THIS list will not appear in a later render of
    // the same list, and retrying would only fight the reader's own choice.
    hasApplied.current = true
    const resolved = resolveRef.current(value)
    if (resolved) {
      onSelectRef.current(resolved)
    }
    // `resolve` and `onSelect` are deliberately NOT dependencies. Callers build
    // them inline (and some are context setters, which RegistrationContext
    // redefines every render), so including them re-runs this effect on every
    // render. The latch above makes that harmless in itself, but the effect also
    // calls a setter — and a set-on-every-render is an update loop. Only the
    // values that decide WHETHER to apply belong here; the functions are read
    // through refs so each run still calls the current one.
  }, [param, enabled, search])
}
